/**
 * Auth Store (Pinia) - 与 admin-web 同一套薄 store 模式.
 *
 * 设计:
 *   - store 只持有业务状态 user + loading; token 留在 localStorage 由 client.ts 管理,
 *     避免 store -> api -> client 的循环依赖.
 *   - isAuthenticated 是 computed(!!getAccessToken()), 与 localStorage 不脱节.
 *   - 401 自动刷新逻辑全在 client.ts 拦截器里, store 不参与 refresh.
 *   - fetchMe() 在 router 首次导航时触发, 拉取当前用户.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { LoginRequest, User } from '@/types/api'
import * as authApi from '@/api/auth'
import { getAccessToken, clearTokens, redirectToLogin } from '@/api/client'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!getAccessToken())
  const displayName = computed(() => user.value?.nickname || user.value?.phone || '将相用户')

  async function login(payload: LoginRequest): Promise<void> {
    loading.value = true
    try {
      const res = await authApi.login(payload)
      user.value = res.user
    } finally {
      loading.value = false
    }
  }

  async function fetchMe(): Promise<void> {
    if (!getAccessToken()) return
    try {
      user.value = await authApi.getMe()
    } catch {
      clearTokens()
      user.value = null
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      user.value = null
      redirectToLogin()
    }
  }

  return { user, loading, isAuthenticated, displayName, login, fetchMe, logout }
})
